// Seo.jsx
// Her sayfanın <head> etiketleri: başlık, açıklama, canonical, hreflang (tr/en/x-default),
// Open Graph / Twitter ve JSON-LD. Prerender betiği (scripts/prerender.mjs) bu etiketleri
// statik HTML'e yazar; böylece JavaScript çalıştırmayan arama motoru ve LLM botları da görür.

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { matchRoute, pathFor, useLang } from '../i18n';
import { SITE_URL, SITE_NAME } from '../config/links';

const OG_IMAGE = `${SITE_URL}/og-image.png`;

export default function Seo({ title, description, jsonLd, noindex = false, image = OG_IMAGE }) {
  const { pathname } = useLocation();
  const { lang } = useLang();
  const route = matchRoute(pathname);

  const trPath = route ? pathFor(route.key, 'tr', route.params) : pathname;
  const enPath = route ? pathFor(route.key, 'en', route.params) : pathname;
  const canonical = SITE_URL + (lang === 'en' ? enPath : trPath);
  const fullTitle = title.includes(SITE_NAME) || title.includes('Shogi Türkiye')
    ? title
    : `${title} | ${lang === 'en' ? 'Shogi Türkiye' : SITE_NAME}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex, follow" /> : null}
      <link rel="canonical" href={canonical} />
      {route && <link rel="alternate" hrefLang="tr" href={SITE_URL + trPath} />}
      {route && <link rel="alternate" hrefLang="en" href={SITE_URL + enPath} />}
      {route && <link rel="alternate" hrefLang="x-default" href={SITE_URL + trPath} />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={lang === 'en' ? 'Shogi Türkiye' : SITE_NAME} />
      <meta property="og:locale" content={lang === 'en' ? 'en_US' : 'tr_TR'} />
      <meta property="og:locale:alternate" content={lang === 'en' ? 'tr_TR' : 'en_US'} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

// Sık kullanılan JSON-LD yardımcıları
export function breadcrumbLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: SITE_URL + it.path,
    })),
  };
}

export function faqLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  };
}
